import React, { useEffect, useState } from 'react';
import { parseJSON, formatDistanceToNow } from 'date-fns';
import { useInView } from 'react-intersection-observer';
import { Button } from '@domain-group/fe-blueprint-ui/button';
import { Stack, StackItem } from '@domain-group/fe-blueprint-ui/layout';
import { Badge } from '@domain-group/fe-blueprint-ui-x/badge';
import { StarRating } from '../../star-rating';
import Address from '../address';
import type { ReviewListEntry } from '../../../../@types';
import * as styles from '../review.style';

export type ReviewProps = ReviewListEntry & {
  onActivate?: (inView: boolean, index: number) => void;
  onVendorRecoModalViewMore: () => void;
  listView: boolean;
  starRating?: number;
  recommendation?: string | undefined;
};

const Review = ({
  index,
  title,
  description,
  reviewedOn,
  streetAddress,
  postcode,
  suburb,
  state,
  listView,
  bedrooms = 0,
  bathrooms = 0,
  carparks = 0,
  descLength = 165,
  starRating = 0,
  recommendation,
  onActivate = () => undefined,
  onVendorRecoModalViewMore,
}: ReviewProps): JSX.Element => {
  const { ref, inView } = useInView({
    threshold: 1,
  });

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (inView) {
      onActivate(inView, index);
    }
  }, [index, inView, onActivate]);

  const timeAgo = formatDistanceToNow(parseJSON(reviewedOn), {
    addSuffix: true,
  });

  const truncatedText =
    description.length > descLength && !isExpanded
      ? `${description.slice(0, descLength)}...`
      : description;

  useEffect(() => {
    if (isExpanded) {
      onVendorRecoModalViewMore();
    }
  }, [isExpanded, onVendorRecoModalViewMore]);

  return (
    <article css={styles.container(listView)} ref={ref}>
      <Stack vertical>
        <StackItem css={styles.headerContainer}>
          <Stack
            horizontalAlign={{
              regular: 'space-between',
              compact: 'start',
            }}
            verticalAlign="center"
            vertical={{
              compact: true,
              regular: false,
            }}
          >
            <StackItem css={styles.title}>
              <Stack verticalAlign="center" gap="xxs">
                <StackItem>{`${starRating.toFixed(1)} `}</StackItem>
                <StackItem css={styles.starRating}>
                  <StarRating rating={starRating} starSize={18} />
                </StackItem>
              </Stack>
            </StackItem>
            <StackItem>
              <Stack gap="xxs">
                {recommendation && (
                  <Badge family="accentThree">
                    {`${recommendation.charAt(0).toUpperCase()}${recommendation.slice(1)} recommendation`}
                  </Badge>
                )}
                <Badge family="accentOne">{timeAgo}</Badge>
              </Stack>
            </StackItem>
          </Stack>
        </StackItem>
        <StackItem css={styles.titleContainer}>
          <h3 css={styles.title}>{title}</h3>
        </StackItem>
        <StackItem css={styles.addressContainer}>
          <Address
            streetAddress={streetAddress}
            suburb={suburb}
            state={state}
            postcode={postcode}
            bedrooms={bedrooms}
            bathrooms={bathrooms}
            carparks={carparks}
          />
        </StackItem>
      </Stack>
      <div>
        {/* Full text for SEO purposes */}
        <span style={{ display: 'none' }}>{description}</span>
        <p data-testid="review-description">
          {truncatedText}
          {description.length > descLength && (
            <Button
              type="button"
              appearance="link"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Show Less' : 'Read More'}
            </Button>
          )}
        </p>
      </div>
    </article>
  );
};

export default Review;
